import {
  IOnlinePurchPriceProfile,
  IPartner,
  IPurchPriceProfile,
  OnlinePartnerBucketGroups,
  PricingTemplate,
} from "../../../../../interfaces/partner.interface";
import {
  OnlinePricingTemplate,
  PartnerBucketGroupPrice,
  PartnerBucketGroups,
} from "../../../../../interfaces/partnerDeal.interface";
import { DEFAULT_PURCH_PRICE_PROFILE } from "../../../utils/defaultPurchPriceProfile";
import { pricedOrUndefined } from "../../../utils/pricedOrUndefined";
import {
  ONLINE_BUCKET_KEYS,
  detectOnlinePartnerPricingStructure,
  getOnlinePartnerGroupKeys,
  onlinePricingTemplateForCountry,
  onlineRangeCardSplit,
  type OnlineBucketKey,
} from "../../../constants/onlineBuckets";
import {
  OnlineBucketGroupCoverageEntry,
  OnlineGroupKey,
  OnlinePricingStructure,
} from "../../../../../interfaces/onlinePricing.interface";
import {
  ONLINE_GROUP_ORDER,
  onlineGroupValueType,
} from "../../../../businesses/utils/sixteenBucketOnlinePricing";

export type InPersonValueType = "Fixed" | "Percentage";

export type InPersonBucketKey =
  | "domesticDebitCards"
  | "europeanDebitCards"
  | "domesticCreditCards"
  | "european"
  | "internationalAndCommercial"
  | "amex";

export interface InPersonBucketState {
  valueType: InPersonValueType;
  floor: number;
  vendor: number;
  partnerDefault: number;
  max: number;
}

export interface InPersonPricingState {
  template: PricingTemplate;
  includeAmex: boolean;
  buckets: Record<InPersonBucketKey, InPersonBucketState>;
  profileName?: string;
  profileWhitelist?: string[];
  // Edit-mode identity: the _id of the edited profile (index 0) plus any other
  // profiles, so serializing round-trips the id instead of orphaning merchants.
  profileId?: string;
  extraProfiles?: IPurchPriceProfile[];
}

export interface BucketTemplateMeta {
  label: string;
  chip: string;
}

export const IN_PERSON_BUCKET_META: Record<
  InPersonBucketKey,
  BucketTemplateMeta
> = {
  domesticDebitCards: { label: "Domestic Debit", chip: "Domestic Debit" },
  europeanDebitCards: { label: "E.E.A Debit", chip: "E.E.A Debit" },
  domesticCreditCards: { label: "Domestic Credit", chip: "Domestic Credit" },
  european: { label: "E.E.A", chip: "E.E.A" },
  internationalAndCommercial: {
    label: "Non E.E.A & Corporate",
    chip: "Non E.E.A & Corporate",
  },
  amex: { label: "AMEX", chip: "AMEX" },
};

export interface CardSplitMeta {
  value: PricingTemplate;
  label: string;
  description: string;
}

/** The three card splits, named once for both rails: the enum is shared, so a
 *  split reading as one name in person and another online described the same
 *  card populations twice. */
export const CARD_SPLIT_OPTIONS: CardSplitMeta[] = [
  {
    value: PricingTemplate.Default,
    label: "Default",
    description:
      "Three card buckets. The standard split for most resellers and full-service deals.",
  },
  {
    value: PricingTemplate.EuropeanDebitSplit,
    label: "E.E.A Debit Split",
    description:
      "Adds a separate E.E.A Debit bucket - useful when E.E.A debit volume is high.",
  },
  {
    value: PricingTemplate.DomesticCreditSplit,
    label: "Domestic Credit Split",
    description: "Adds a separate Domestic Credit bucket alongside debit.",
  },
];

// Ordered bucket keys per template (amex appended separately when enabled).
export const orderedBucketKeys = (
  template: PricingTemplate,
  includeAmex: boolean
): InPersonBucketKey[] => {
  const base: InPersonBucketKey[] = ["domesticDebitCards"];
  if (template === PricingTemplate.EuropeanDebitSplit)
    base.push("europeanDebitCards");
  if (template === PricingTemplate.DomesticCreditSplit)
    base.push("domesticCreditCards");
  base.push("european", "internationalAndCommercial");
  if (includeAmex) base.push("amex");
  return base;
};

const DEFAULT_BUCKET: Record<InPersonBucketKey, InPersonBucketState> = {
  domesticDebitCards: {
    valueType: "Fixed",
    floor: 0.06,
    vendor: 0,
    partnerDefault: 0.06,
    max: 0.3,
  },
  europeanDebitCards: {
    valueType: "Percentage",
    floor: 1.1,
    vendor: 0,
    partnerDefault: 1.1,
    max: 2.8,
  },
  domesticCreditCards: {
    valueType: "Percentage",
    floor: 1.1,
    vendor: 0,
    partnerDefault: 1.1,
    max: 2.8,
  },
  european: {
    valueType: "Percentage",
    floor: 1.39,
    vendor: 0,
    partnerDefault: 1.39,
    max: 2.8,
  },
  internationalAndCommercial: {
    valueType: "Percentage",
    floor: 2.29,
    vendor: 0,
    partnerDefault: 2.29,
    max: 4.5,
  },
  amex: {
    valueType: "Percentage",
    floor: 3.3,
    vendor: 0,
    partnerDefault: 3.3,
    max: 4,
  },
};

const bucketFromExisting = (
  key: InPersonBucketKey,
  floorPrice?: PartnerBucketGroupPrice,
  defaultPrice?: PartnerBucketGroupPrice,
  vendorPrice?: PartnerBucketGroupPrice
): InPersonBucketState => {
  const fallback = DEFAULT_BUCKET[key];
  const valueType = floorPrice?.valueType || fallback.valueType;
  const floor = floorPrice?.value ?? fallback.floor;
  return {
    valueType,
    floor,
    vendor: vendorPrice?.value ?? 0,
    partnerDefault: defaultPrice?.value ?? floor,
    max: floorPrice?.max ?? fallback.max,
  };
};

// Build initial step state from an existing partner (edit) or defaults (create).
export const buildInPersonState = (
  partner?: Partial<IPartner>,
  options: { includeAmex?: boolean } = {}
): InPersonPricingState => {
  const profiles = partner?.purchPriceProfiles;
  const profile = profiles?.[0];
  const template =
    profile?.pricingTemplate ||
    partner?.pricingTemplate ||
    PricingTemplate.Default;
  const floorGroups = profile?.bucketGroups;
  // PC-926: includeAmex follows hideInPersonAmex (default include when unset).
  const includeAmex = options.includeAmex ?? true;

  const buckets = {} as Record<InPersonBucketKey, InPersonBucketState>;
  (Object.keys(DEFAULT_BUCKET) as InPersonBucketKey[]).forEach((key) => {
    buckets[key] = bucketFromExisting(
      key,
      floorGroups?.[key],
      // Per range, falling back per bucket to the partner-level section.
      pricedOrUndefined(profile?.defaultPricing?.[key]) ??
        partner?.defaultPricing?.[key],
      pricedOrUndefined(profile?.softwareVendorPricing?.[key]) ??
        partner?.softwareVendorPricing?.[key]
    );
  });

  return {
    template,
    includeAmex,
    buckets,
    profileName: profile?.name,
    profileWhitelist: profile?.whitelist,
    profileId: profile?._id || undefined,
    extraProfiles: profiles?.slice(1),
  };
};

export const toFloorGroups = (
  state: InPersonPricingState
): IPurchPriceProfile => {
  const keys = orderedBucketKeys(state.template, state.includeAmex);
  const bucketGroups: Record<string, PartnerBucketGroupPrice> = {};
  keys.forEach((key) => {
    const b = state.buckets[key];
    bucketGroups[key] = { value: b.floor, valueType: b.valueType, max: b.max };
  });
  // Create: omit _id (empty string fails ObjectId cast; Mongoose generates one).
  // Edit: round-trip the existing _id so merchant->profile links survive.
  const profile = {
    name: state.profileName || DEFAULT_PURCH_PRICE_PROFILE.name || "default",
    pricingTemplate: state.template,
    bucketGroups: bucketGroups as unknown as IPurchPriceProfile["bucketGroups"],
    // The range owns these, so a save touches no other range.
    defaultPricing: toBucketGroups(state, (b) => b.partnerDefault),
    softwareVendorPricing: toBucketGroups(state, (b) => b.vendor),
  } as IPurchPriceProfile;
  if (state.profileId) profile._id = state.profileId;
  if (state.profileWhitelist?.length)
    profile.whitelist = state.profileWhitelist;
  return profile;
};

export const toBucketGroups = (
  state: InPersonPricingState,
  pick: (b: InPersonBucketState) => number
): PartnerBucketGroups => {
  const keys = orderedBucketKeys(state.template, state.includeAmex);
  const groups: Record<string, PartnerBucketGroupPrice> = {};
  keys.forEach((key) => {
    groups[key] = {
      value: pick(state.buckets[key]),
      valueType: state.buckets[key].valueType,
    };
  });
  return groups as unknown as PartnerBucketGroups;
};

// The one range that speaks for the partner. A named range prices a subset of
// merchants, so nothing it carries belongs on a partner-level field.
const isPartnerDefaultRange = (state: { profileName?: string }): boolean =>
  !state.profileName ||
  state.profileName.trim().toLowerCase() ===
    (DEFAULT_PURCH_PRICE_PROFILE.name || "default");

// Map step state onto the partner draft fields the backend expects.
export const inPersonStateToPartnerFields = (
  state: InPersonPricingState,
  prior?: Partial<IPartner>
): Partial<IPartner> => {
  const floor = toFloorGroups(state);

  // When Amex is hidden, keep any previously stored Amex rather than wiping it.
  if (!state.includeAmex && prior) {
    const priorRange = prior.purchPriceProfiles?.[0];
    const priorFloorAmex = priorRange?.bucketGroups?.amex;
    if (priorFloorAmex && floor.bucketGroups) {
      floor.bucketGroups = {
        ...floor.bucketGroups,
        amex: priorFloorAmex,
      };
    }
    const priorDefaultAmex =
      priorRange?.defaultPricing?.amex ?? prior.defaultPricing?.amex;
    if (priorDefaultAmex && floor.defaultPricing) {
      floor.defaultPricing = {
        ...floor.defaultPricing,
        amex: priorDefaultAmex,
      };
    }
    const priorVendorAmex =
      priorRange?.softwareVendorPricing?.amex ??
      prior.softwareVendorPricing?.amex;
    if (priorVendorAmex && floor.softwareVendorPricing) {
      floor.softwareVendorPricing = {
        ...floor.softwareVendorPricing,
        amex: priorVendorAmex,
      };
    }
  }

  const fields: Partial<IPartner> = {
    purchPriceProfiles: [floor, ...(state.extraProfiles ?? [])],
  };
  // A named range must not move the partner-level fields other ranges fall back to.
  if (!isPartnerDefaultRange(state)) return fields;
  fields.pricingTemplate = state.template;
  fields.softwareVendorPricingTemplate = state.template;
  fields.defaultPricing = floor.defaultPricing as PartnerBucketGroups;
  fields.softwareVendorPricing =
    floor.softwareVendorPricing as PartnerBucketGroups;
  return fields;
};

interface BuildInPersonRangeUpdateParams {
  state: InPersonPricingState;
  existingProfiles: IPurchPriceProfile[];
  name: string;
  whitelist: string[];
}

// Creating a range appends it and touches nothing else: the range carries its
// own merchant default and vendor markup, so no pre-existing range moves.
export const buildInPersonRangeUpdate = ({
  state,
  existingProfiles,
  name,
  whitelist,
}: BuildInPersonRangeUpdateParams): Partial<IPartner> => {
  const newProfile = toFloorGroups({
    ...state,
    profileId: undefined,
    profileName: name,
    profileWhitelist: whitelist,
  });
  return { purchPriceProfiles: [...existingProfiles, newProfile] };
};

export interface BucketValidationError {
  key: InPersonBucketKey;
  message: string;
}

export const validateInPersonState = (
  state: InPersonPricingState
): BucketValidationError[] => {
  const keys = orderedBucketKeys(state.template, state.includeAmex);
  const errors: BucketValidationError[] = [];
  keys.forEach((key) => {
    const b = state.buckets[key];
    if (b.partnerDefault < b.floor) {
      errors.push({
        key,
        message: "Partner default is below the Klearly floor",
      });
    } else if (b.partnerDefault > b.max) {
      errors.push({ key, message: "Partner default is above the max cap" });
    }
    if (b.vendor < 0 || b.vendor > b.floor) {
      errors.push({
        key,
        message: "SW vendor price must be between 0 and the floor",
      });
    }
  });
  return errors;
};

// ── Online pricing ──────────────────────────────────────────────────────────
// Online reuses the same per-bucket shape (floor/vendor/partner default/max);
// the backend stores it as OnlinePartnerBucketGroups (value + valueType, with
// floor/max persisted to the optional min/max fields).

// Re-exported from the canonical bucket-key source so consumers of this module
// (e.g. PartnerOnlinePricingStep) keep a single OnlineBucketKey definition.
export type { OnlineBucketKey };

// Full bucket-key universe, used to build/initialize state so every key has a
// value regardless of which subset the current template actually needs (see
// getOnlineBucketKeysForTemplate for the template-specific subset used by
// rendering/validation/submission).
export const ONLINE_BUCKET_ORDER: readonly OnlineBucketKey[] =
  ONLINE_BUCKET_KEYS;

export const ONLINE_BUCKET_META: Record<OnlineBucketKey, BucketTemplateMeta> = {
  iDeal: { label: "iDEAL", chip: "iDEAL" },
  bancontact: { label: "Bancontact", chip: "Bancontact" },
  amex: { label: "AMEX", chip: "AMEX" },
  corporateCreditCards: {
    label: "Corporate Credit Cards",
    chip: "Corporate Credit Cards",
  },
  nonCorporateCreditCards: {
    label: "Non-Corporate Credit Cards",
    chip: "Non-Corporate Credit Cards",
  },
  appleGooglePay: {
    label: "Apple Pay / Google Pay",
    chip: "Apple Pay / Google Pay",
  },
};

// Default unit per group when nothing is stored yet. Every card group and the
// wallet are a percentage, iDEAL a fixed euro fee; `bancontact` may be either
// and starts fixed, as the four-bucket form has always shown it.
const onlineDefaultValueType = (key: OnlineGroupKey): InPersonValueType =>
  onlineGroupValueType(key) ?? "Fixed";

// State is initialized over every group key either shape can price, so switching
// structure, card split or local method never reads an absent bucket. Which
// subset is rendered, validated and submitted is always the caller's key set —
// see onlineGroupKeysForState.
const ALL_ONLINE_GROUP_KEYS: readonly OnlineGroupKey[] = [
  ...ONLINE_GROUP_ORDER,
  "corporateCreditCards",
  "nonCorporateCreditCards",
];

/** A new range prices nothing until the operator fills it in. */
export const blankOnlineBuckets = (): Record<
  OnlineGroupKey,
  InPersonBucketState
> => {
  const buckets = {} as Record<OnlineGroupKey, InPersonBucketState>;
  ALL_ONLINE_GROUP_KEYS.forEach((key) => {
    buckets[key] = {
      valueType: onlineDefaultValueType(key),
      floor: 0,
      vendor: 0,
      partnerDefault: 0,
      max: 0,
    };
  });
  return buckets;
};

export interface OnlinePricingState {
  buckets: Record<OnlineGroupKey, InPersonBucketState>;
  pricingTemplate: OnlinePricingTemplate;
  // Which shape this partner is priced on. Four-bucket unless the stored
  // sections prove otherwise, so a partner that predates the 16-bucket
  // structure keeps the form it has always had.
  structure: OnlinePricingStructure;
  // The 16-bucket card split this range declares, independent of the in-person
  // rail — see onlineRangeCardSplit. Ignored on the four-bucket path, which
  // prices no card groups for a split to regroup.
  cardSplit: PricingTemplate;
  // Group set from the read. Create and a template switch have none, and then
  // the selected split plus local method name the rows.
  coverage?: OnlineBucketGroupCoverageEntry[];
  storedGroups?: OnlinePartnerBucketGroups;
  // Each group's software vendor fee as read, so a 16-bucket save can tell
  // whether the operator changed it — see partnerSoftwareVendorGroups.
  loadedVendorFees?: Partial<
    Record<OnlineGroupKey, Pick<InPersonBucketState, "vendor" | "valueType">>
  >;
  // A 16-bucket form over a fee still stored per cardholder type. The card rows
  // start from it, and the step says a fee save replaces it.
  fourBucketVendorFee?: FourBucketCardFee;
  profileName?: string;
  profileWhitelist?: string[];
  profileId?: string;
  extraProfiles?: IOnlinePurchPriceProfile[];
}

const isSixteenBucket = (state: OnlinePricingState): boolean =>
  state.structure === OnlinePricingStructure.SixteenBucket;

type FourBucketCardKey = "corporateCreditCards" | "nonCorporateCreditCards";
type OnlineFee = Pick<InPersonBucketState, "vendor" | "valueType">;
export type FourBucketCardFee = Partial<Record<FourBucketCardKey, OnlineFee>>;

export const formatOnlineFee = ({ vendor, valueType }: OnlineFee): string => {
  const value = vendor === 0 ? "0" : vendor.toFixed(3).replace(/\.?0+$/, "");
  return valueType === "Fixed" ? `€${value}` : `${value}%`;
};

const vendorFeesOf = (
  buckets: Record<OnlineGroupKey, InPersonBucketState>
): OnlinePricingState["loadedVendorFees"] =>
  Object.fromEntries(
    ALL_ONLINE_GROUP_KEYS.map((key) => [
      key,
      { vendor: buckets[key].vendor, valueType: buckets[key].valueType },
    ])
  );

// Where a four-bucket card fee lands on 16 buckets. A consumer payment can fall
// in any card group, and every corporate one falls in international and
// commercial, which is the group the backend bounds the corporate fee by.
const FOUR_BUCKET_FEE_FOR_CARD_GROUP: Partial<
  Record<OnlineGroupKey, FourBucketCardKey>
> = {
  domesticDebitCards: "nonCorporateCreditCards",
  domesticCreditCards: "nonCorporateCreditCards",
  europeanDebitCards: "nonCorporateCreditCards",
  european: "nonCorporateCreditCards",
  internationalAndCommercial: "corporateCreditCards",
};

const fourBucketCardFee = (
  read: (key: FourBucketCardKey) => OnlineFee | undefined
): FourBucketCardFee | undefined => {
  const fee: FourBucketCardFee = {};
  (["corporateCreditCards", "nonCorporateCreditCards"] as const).forEach(
    (key) => {
      const price = read(key);
      if (price && price.vendor > 0) fee[key] = price;
    }
  );
  return Object.keys(fee).length > 0 ? fee : undefined;
};

/** Card rows a four-bucket fee seeds: unset rows only, and a priced row only in
 *  the fee's own unit. Returns the rows it filled. */
const seedCardFeesFromFourBucket = (
  buckets: Record<OnlineGroupKey, InPersonBucketState>,
  fee: FourBucketCardFee
): OnlineGroupKey[] => {
  const seeded: OnlineGroupKey[] = [];
  (Object.keys(FOUR_BUCKET_FEE_FOR_CARD_GROUP) as OnlineGroupKey[]).forEach(
    (key) => {
      const price = fee[FOUR_BUCKET_FEE_FOR_CARD_GROUP[key]!];
      const bucket = buckets[key];
      if (!price || bucket.vendor > 0) return;
      const unpriced = !(bucket.floor > 0);
      if (!unpriced && bucket.valueType !== price.valueType) return;
      buckets[key] = {
        ...bucket,
        vendor: price.vendor,
        valueType: unpriced ? price.valueType : bucket.valueType,
      };
      seeded.push(key);
    }
  );
  return seeded;
};

/** The groups this state prices and writes. The Online step table renders the
 *  listed subset; band checks follow that same set so a hidden group cannot
 *  refuse a save with no row to highlight. */
export const onlineGroupKeysForState = (
  state: OnlinePricingState
): readonly OnlineGroupKey[] =>
  getOnlinePartnerGroupKeys({
    structure: state.structure,
    template: state.pricingTemplate,
    coverage: state.coverage,
    stored: state.storedGroups,
    cardSplit: state.cardSplit,
  });

// The wallet is not a listed group on the partner Online step. A stored rate
// still saves with the range; only the table and info-box chips drop it.
export const listedOnlineGroupKeysForState = (
  state: OnlinePricingState
): readonly OnlineGroupKey[] =>
  onlineGroupKeysForState(state).filter((key) => key !== "appleGooglePay");

export const buildOnlineState = (
  partner?: Partial<IPartner>,
  country?: string,
  options: { structure?: OnlinePricingStructure } = {}
): OnlinePricingState => {
  const profiles = partner?.onlinePurchPriceProfiles;
  const profile = profiles?.[0];
  const floorGroups = profile?.bucketGroups;
  // What the partner is actually stored as decides the form. Reading the four
  // -bucket key list off a 16-bucket partner is what rendered it as rows of
  // zeros, and then wrote those zeros back over its real pricing.
  const stored = detectOnlinePartnerPricingStructure(
    floorGroups,
    profile?.defaultOnlinePricing,
    partner?.defaultOnlinePricing,
    partner?.softwareVendorOnlinePricing
  );
  // The shape the backend offers a partner with nothing frozen in Adyen. Stands
  // in only where nothing stored proves a structure, so a partner already
  // priced on four buckets still opens on the form it is priced in.
  const seed = stored ? undefined : partner?.defaultOnlinePriceRange;
  const structure =
    options.structure ??
    stored ??
    seed?.onlinePricingStructure ??
    OnlinePricingStructure.FourBucket;
  const sixteenBucket = structure === OnlinePricingStructure.SixteenBucket;
  const buckets = {} as Record<OnlineGroupKey, InPersonBucketState>;
  ALL_ONLINE_GROUP_KEYS.forEach((key) => {
    const floorPrice = floorGroups?.[key];
    const defPrice =
      profile?.defaultOnlinePricing?.[key] ??
      partner?.defaultOnlinePricing?.[key];
    const vendorPrice =
      profile?.softwareVendorOnlinePricing?.[key] ??
      partner?.softwareVendorOnlinePricing?.[key];
    const floor = floorPrice?.value ?? defPrice?.min ?? 0;
    buckets[key] = {
      // A four-bucket card key on a 16-bucket partner has no floor, and the
      // fee is the only section a save still writes for it, so the fee's unit
      // wins over a partner default the save leaves stale.
      valueType:
        floorPrice?.valueType ||
        (sixteenBucket ? vendorPrice?.valueType : undefined) ||
        defPrice?.valueType ||
        seed?.bucketGroups?.[key]?.valueType ||
        onlineDefaultValueType(key),
      floor,
      vendor: vendorPrice?.value ?? 0,
      // An unstored partner default reads as the floor, as in-person does: zero
      // against a priced floor is a band nothing can be priced inside.
      partnerDefault: defPrice?.value ?? floor,
      max: floorPrice?.max ?? defPrice?.max ?? 0,
    };
  });
  // A 16-bucket form over a fee still stored per cardholder type would show its
  // card rows at zero, and the first fee save would write those zeros over it.
  const vendorSection = partner?.softwareVendorOnlinePricing;
  const fourBucketVendorFee =
    sixteenBucket &&
    !Object.keys(FOUR_BUCKET_FEE_FOR_CARD_GROUP).some(
      (key) => vendorSection?.[key as OnlineGroupKey]
    )
      ? fourBucketCardFee((key) => {
          const price = vendorSection?.[key];
          return price && { vendor: price.value, valueType: price.valueType };
        })
      : undefined;
  if (fourBucketVendorFee)
    seedCardFeesFromFourBucket(buckets, fourBucketVendorFee);
  return {
    buckets,
    structure,
    fourBucketVendorFee,
    coverage: profile?.onlineBucketGroupCoverage,
    storedGroups: floorGroups ?? seed?.bucketGroups,
    // Per range, and absent reads as Default — so a range the operator has not
    // declared a split on shows the split it is actually priced under.
    cardSplit: seed?.onlineCardSplitTemplate ?? onlineRangeCardSplit(profile),
    // Existing profile/partner template wins (edit mode); otherwise derive
    // from the partner's country (create mode) instead of always defaulting
    // to Default/NL, so a Belgian partner starts out on BancontactBE.
    pricingTemplate:
      profile?.pricingTemplate ??
      partner?.defaultOnlinePricingTemplate ??
      seed?.pricingTemplate ??
      onlinePricingTemplateForCountry(country),
    loadedVendorFees: vendorFeesOf(buckets),
    profileName: profile?.name,
    profileWhitelist: profile?.whitelist,
    profileId: profile?._id,
    extraProfiles: profiles?.slice(1),
  };
};

const isOnlineBucketConfigured = (bucket: InPersonBucketState): boolean =>
  bucket.floor > 0 ||
  bucket.vendor > 0 ||
  bucket.partnerDefault > 0 ||
  bucket.max > 0;

export const hasAnyOnlinePricing = (state: OnlinePricingState): boolean =>
  onlineGroupKeysForState(state).some((key) =>
    isOnlineBucketConfigured(state.buckets[key])
  );

export const ONLINE_PRICING_FIELD_KEYS = [
  "defaultOnlinePricing",
  "defaultOnlinePricingTemplate",
  "softwareVendorOnlinePricing",
  "softwareVendorOnlinePricingTemplate",
  "onlinePurchPriceProfiles",
] as const;

/** Drop the online-deal fields so a payload cannot recreate them. */
export const omitOnlinePricingFields = (
  partner: Partial<IPartner>
): Partial<IPartner> => {
  const next: Partial<IPartner> = { ...partner };
  ONLINE_PRICING_FIELD_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
};

/** Why a blocked online save was refused. A range that prices nothing at all
 *  highlights nothing — the step suppresses the missing-floor errors there — so
 *  it is refused for what it is rather than pointed at values that are not red. */
export const onlineSaveRefusal = (state: OnlinePricingState): string =>
  hasAnyOnlinePricing(state)
    ? "Please fix the highlighted pricing values before continuing."
    : "Price at least one bucket before saving the online pricing.";

// Which groups a save writes. A 16-bucket section must arrive whole — the
// backend rejects a payload missing a group its template pair prices, and
// dropping one because it reads as "unconfigured" is how a partner's stored
// rate goes missing. The four-bucket path keeps its long-standing behaviour of
// writing only the buckets that carry something, so a partner with a bucket it
// never priced does not gain a zero band.
export const onlineGroupsToWrite = (
  state: OnlinePricingState
): readonly OnlineGroupKey[] => {
  const keys = onlineGroupKeysForState(state);
  return isSixteenBucket(state)
    ? keys
    : keys.filter((key) => isOnlineBucketConfigured(state.buckets[key]));
};

// The wallet is the one group Klearly's cut is not gated on, either shape — see
// the backend's missingPartnerOnlineFloorBuckets, which excludes it by name.
const FLOOR_EXEMPT_GROUPS: readonly OnlineGroupKey[] = ["appleGooglePay"];

/** Groups a 16-bucket range would store at a floor of zero. The section is
 *  written whole, and every reader takes that zero as a real floor, so the
 *  merchant reaches Adyen with Klearly's cut on the group set to nothing. */
export const unflooredOnlineGroups = (
  state: OnlinePricingState
): readonly OnlineGroupKey[] =>
  isSixteenBucket(state)
    ? onlineGroupKeysForState(state).filter(
        (key) =>
          !FLOOR_EXEMPT_GROUPS.includes(key) && !(state.buckets[key].floor > 0)
      )
    : [];

const LOCAL_METHOD_KEYS: OnlineGroupKey[] = ["iDeal", "bancontact"];
const EMPTY_LOCAL_BUCKET: InPersonBucketState = {
  valueType: "Fixed",
  floor: 0,
  vendor: 0,
  partnerDefault: 0,
  max: 0,
};

// Re-deriving the template on a country change must not lose whatever the admin
// already typed: card buckets (amex/corporate/non-corporate/appleGooglePay) are
// shared across every template, so only the local-method bucket (iDeal vs
// bancontact) needs to move — its value carries across to the new key, the old
// key is cleared. A no-op (same reference back) when the template already matches.
export const remapLocalMethodForCountry = (
  state: OnlinePricingState,
  country?: string
): OnlinePricingState => {
  const nextTemplate = onlinePricingTemplateForCountry(country);
  if (state.pricingTemplate === nextTemplate) return state;

  const nextLocalKey = onlineGroupKeysForState({
    ...state,
    pricingTemplate: nextTemplate,
  }).find((key) => LOCAL_METHOD_KEYS.includes(key));
  const carried = LOCAL_METHOD_KEYS.map((key) => state.buckets[key]).find(
    (bucket) => isOnlineBucketConfigured(bucket)
  );

  const buckets = { ...state.buckets };
  LOCAL_METHOD_KEYS.forEach((key) => {
    if (key !== nextLocalKey) buckets[key] = EMPTY_LOCAL_BUCKET;
  });
  if (nextLocalKey) buckets[nextLocalKey] = carried ?? buckets[nextLocalKey];

  return { ...state, pricingTemplate: nextTemplate, buckets };
};

// Partial by design, unlike `bucketGroups`: the Adyen submit gate reads a group
// priced at nothing as a cap of zero (fillOnlineBandsFromPartnerDefault), so a
// group carrying nothing is left out rather than banded at zero.
const toOnlineDefaultGroups = (
  state: OnlinePricingState
): OnlinePartnerBucketGroups => {
  const groups = {} as OnlinePartnerBucketGroups;
  onlineGroupsToWrite(state).forEach((key) => {
    const bucket = state.buckets[key];
    if (!isOnlineBucketConfigured(bucket)) return;
    const price: PartnerBucketGroupPrice = {
      value: bucket.partnerDefault,
      valueType: bucket.valueType,
    };
    if (bucket.floor > 0) price.min = bucket.floor;
    if (bucket.max > 0) price.max = bucket.max;
    groups[key] = price;
  });
  return groups;
};

const toOnlineVendorGroups = (
  state: OnlinePricingState
): OnlinePartnerBucketGroups => {
  const groups = {} as OnlinePartnerBucketGroups;
  onlineGroupsToWrite(state).forEach((key) => {
    const bucket = state.buckets[key];
    groups[key] = { value: bucket.vendor, valueType: bucket.valueType };
  });
  return groups;
};

/** Whether a software vendor fee typed on this range is saved anywhere. A
 *  four-bucket range stores its own; a 16-bucket range cannot, and only the
 *  partner's default range writes the partner-level fee, because editPartner
 *  checks that section against the default range's card split. */
export const savesOnlineVendorFee = (state: OnlinePricingState): boolean =>
  !isSixteenBucket(state) || isPartnerDefaultRange(state);

/** The range moved onto another template. On a card split change within 16
 *  buckets, a group the split newly prices starts from the `european` fee: the
 *  group it was split off from, which is what editPartner carries it to and
 *  both readers fall back to, so the row shows the fee a save would store. */
export const withOnlineTemplateAxes = (
  state: OnlinePricingState,
  axes: Pick<OnlinePricingState, "structure" | "cardSplit">
): OnlinePricingState => {
  const next = { ...state, ...axes, coverage: undefined };
  if (!isSixteenBucket(next)) return next;
  // Moving onto 16 buckets leaves the partner fee stored per cardholder type
  // until a fee is saved, so the card rows start from it as a reopen would.
  // Only a fee edited before the move counts as a change.
  if (!isSixteenBucket(state)) {
    const fee = fourBucketCardFee((key) => state.buckets[key]);
    if (!fee) return next;
    const buckets = { ...next.buckets };
    const loadedVendorFees = { ...next.loadedVendorFees };
    seedCardFeesFromFourBucket(buckets, fee).forEach((key) => {
      const source = FOUR_BUCKET_FEE_FOR_CARD_GROUP[key]!;
      const loaded = state.loadedVendorFees?.[source];
      if (
        loaded?.vendor === state.buckets[source].vendor &&
        loaded?.valueType === state.buckets[source].valueType
      ) {
        loadedVendorFees[key] = {
          vendor: buckets[key].vendor,
          valueType: buckets[key].valueType,
        };
      }
    });
    return {
      ...next,
      buckets,
      loadedVendorFees,
      fourBucketVendorFee: fee,
    };
  }
  const before = new Set(listedOnlineGroupKeysForState(state));
  const european = state.buckets.european;
  if (!(european.vendor > 0)) return next;
  const buckets = { ...next.buckets };
  listedOnlineGroupKeysForState(next).forEach((key) => {
    const bucket = buckets[key];
    if (before.has(key) || bucket.vendor > 0) return;
    const unpriced = !(bucket.floor > 0);
    if (!unpriced && bucket.valueType !== european.valueType) return;
    buckets[key] = {
      ...bucket,
      vendor: european.vendor,
      valueType: unpriced ? european.valueType : bucket.valueType,
    };
  });
  return { ...next, buckets };
};

/** A save that wrote the partner fee: what is on screen is now what is stored.
 *  The state is not re-read after a save, so without this a fee cleared later
 *  in the same session compares equal to the old load and is never sent. */
export const withSavedOnlineVendorFees = (
  state: OnlinePricingState
): OnlinePricingState => ({
  ...state,
  loadedVendorFees: vendorFeesOf(state.buckets),
  fourBucketVendorFee: undefined,
});

const onlineVendorFeeChanged = (state: OnlinePricingState): boolean =>
  listedOnlineGroupKeysForState(state).some((key) => {
    const bucket = state.buckets[key];
    const loaded = state.loadedVendorFees?.[key];
    return (
      bucket.vendor !== (loaded?.vendor ?? 0) ||
      (bucket.vendor > 0 && bucket.valueType !== loaded?.valueType)
    );
  });

// The partner-level fee. A four-bucket default range writes it as it always
// has, zeros included. On 16 buckets editPartner takes the section whole —
// every group of the default range's template pair, never mixed with the
// four-bucket card keys — so it is written whole, zeros included, which is also
// how a cleared fee lands. It is sent only once the operator changed a fee: a
// stored four-bucket fee left alone stays readable on its corporate and
// consumer keys. The wallet is neither a row nor required, so only a fee above
// zero rides along.
const partnerSoftwareVendorGroups = (
  state: OnlinePricingState
): OnlinePartnerBucketGroups | undefined => {
  if (!isPartnerDefaultRange(state)) return undefined;
  if (!isSixteenBucket(state)) return toOnlineVendorGroups(state);
  if (!onlineVendorFeeChanged(state)) return undefined;
  const groups = {} as OnlinePartnerBucketGroups;
  listedOnlineGroupKeysForState(state).forEach((key) => {
    const bucket = state.buckets[key];
    groups[key] = { value: bucket.vendor, valueType: bucket.valueType };
  });
  const wallet = state.buckets.appleGooglePay;
  if (wallet?.vendor > 0) {
    groups.appleGooglePay = {
      value: wallet.vendor,
      valueType: wallet.valueType,
    };
  }
  return Object.keys(groups).length > 0 ? groups : undefined;
};

export const toOnlineFloorGroups = (
  state: OnlinePricingState
): IOnlinePurchPriceProfile => {
  const bucketGroups: Partial<OnlinePartnerBucketGroups> = {};
  onlineGroupsToWrite(state).forEach((key) => {
    const bucket = state.buckets[key];
    const price: PartnerBucketGroupPrice = {
      value: bucket.floor,
      valueType: bucket.valueType,
    };
    if (bucket.max > 0) price.max = bucket.max;
    bucketGroups[key] = price;
  });
  const profile = {
    name: state.profileName || "default",
    bucketGroups,
    defaultOnlinePricing: toOnlineDefaultGroups(state),
    pricingTemplate: state.pricingTemplate,
  } as IOnlinePurchPriceProfile;
  // A 16-bucket range must not carry this section: the preview prefers it and
  // rejects the shape. The fee stays on the partner.
  if (!isSixteenBucket(state))
    profile.softwareVendorOnlinePricing = toOnlineVendorGroups(state);
  // Only a 16-bucket range prices card groups, so only it has a split to
  // declare; a four-bucket range left undeclared reads as Default and regroups
  // nothing.
  if (isSixteenBucket(state)) profile.onlineCardSplitTemplate = state.cardSplit;
  if (state.profileId) profile._id = state.profileId;
  if (state.profileWhitelist?.length)
    profile.whitelist = state.profileWhitelist;
  return profile;
};

export const onlineStateToPartnerFields = (
  state: OnlinePricingState
): Partial<IPartner> => {
  // An unpriced range is not a deal. Writing zeros or the offered default
  // range recreates online pricing the operator never entered.
  if (!hasAnyOnlinePricing(state) && !state.extraProfiles?.length) return {};
  const fields: Partial<IPartner> = {
    onlinePurchPriceProfiles: [
      toOnlineFloorGroups(state),
      ...(state.extraProfiles ?? []),
    ],
  };
  const vendor = partnerSoftwareVendorGroups(state);
  if (vendor) fields.softwareVendorOnlinePricing = vendor;
  if (!isPartnerDefaultRange(state)) return fields;
  // Names the local payment method, not a bucket, so either structure can carry
  // it — and it is the only place that axis is recorded partner-wide. The
  // software-vendor one has no per-range fallback anywhere in the read path,
  // while Adyen onboarding and the markup report both group off it, so leaving
  // it unwritten prices a Belgian partner's merchants on iDeal.
  fields.defaultOnlinePricingTemplate = state.pricingTemplate;
  fields.softwareVendorOnlinePricingTemplate = state.pricingTemplate;
  // A 16-bucket range cannot store default pricing in its own keys.
  if (isSixteenBucket(state)) return fields;
  fields.defaultOnlinePricing = toOnlineDefaultGroups(state);
  return fields;
};

/** The fields an online save writes. The card split is declared on the range
 *  itself, so an online save no longer has to drag the in-person section along
 *  to keep the two rails describing the same card populations. */
export const onlineSaveFields = (
  state: OnlinePricingState
): Partial<IPartner> => onlineStateToPartnerFields(state);

interface BuildOnlineRangeUpdateParams {
  state: OnlinePricingState;
  existingProfiles: IOnlinePurchPriceProfile[];
  name: string;
  whitelist: string[];
}

export const buildOnlineRangeUpdate = ({
  state,
  existingProfiles,
  name,
  whitelist,
}: BuildOnlineRangeUpdateParams): Partial<IPartner> => {
  const newProfile = toOnlineFloorGroups({
    ...state,
    profileId: undefined,
    profileName: name,
    profileWhitelist: whitelist,
  });

  return {
    onlinePurchPriceProfiles: [...existingProfiles, newProfile],
  };
};

export const prioritizeOnlineProfileForView = (
  profiles: IOnlinePurchPriceProfile[],
  activeProfileId?: string
): IOnlinePurchPriceProfile[] => {
  const targetIndex = activeProfileId
    ? profiles.findIndex((profile) => profile._id === activeProfileId)
    : profiles.findIndex((profile) => profile.name === "default");
  if (targetIndex < 0) return profiles;

  return [
    profiles[targetIndex],
    ...profiles.filter((_profile, index) => index !== targetIndex),
  ];
};

export interface OnlineValidationError {
  key: OnlineGroupKey;
  message: string;
  // Blocks a save even on a bucket the operator never touched: the group carries
  // no floor at all, so there is no stored value the panel is second-guessing.
  unconditional?: boolean;
}

export const validateOnlineState = (
  state: OnlinePricingState
): OnlineValidationError[] => {
  const errors: OnlineValidationError[] = [];
  unflooredOnlineGroups(state).forEach((key) => {
    errors.push({
      key,
      message: "Klearly floor must be above zero",
      unconditional: true,
    });
  });
  // Band and vendor checks highlight a row. Unlisted groups have none, so a
  // stored wallet rate that fails them would refuse the save with nothing to
  // fix. Floor-zero already exempts the wallet by name.
  listedOnlineGroupKeysForState(state).forEach((key) => {
    const b = state.buckets[key];
    if (b.vendor < 0 || (b.floor > 0 && b.vendor > b.floor)) {
      // Without the column the row's fee is the partner's, set on the default
      // range, so the message has to say which fee and where it is changed.
      errors.push({
        key,
        message: savesOnlineVendorFee(state)
          ? "SW vendor price must be between 0 and the floor"
          : `Klearly floor is below the partner's SW vendor fee of ${formatOnlineFee(b)}, which is set on the default range`,
      });
    }
    if (!isOnlineBucketConfigured(b)) return;
    // Floor and max are the band a merchant is priced within, so a floor above
    // the cap describes a range nothing can be priced inside.
    if (b.max > 0 && b.floor > b.max) {
      errors.push({ key, message: "Klearly floor is above the max cap" });
    }
    if (b.partnerDefault < b.floor) {
      errors.push({
        key,
        message: "Partner default is below the Klearly floor",
      });
    } else if (b.max > 0 && b.partnerDefault > b.max) {
      errors.push({ key, message: "Partner default is above the max cap" });
    }
  });
  return errors;
};
